import {
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ShipperConfigPopupComponent } from 'src/app/components/shipper-config/shipper-config-popup/shipper-config-popup.component';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  mergeMap,
  of,
  switchMap,
  take,
} from 'rxjs';
import { AdminSearchCriteria, SpecialCharacters } from 'src/app/core/enums';
import { UserAdmin } from 'src/app/core/models/admin/user-admin.model';
import { Items } from 'src/app/core/models/common/items.model';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import { ShipperConfig } from 'src/app/core/models/sci/shipper-config.model';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { ErrorhandleService } from 'src/app/core/services/error-handling/error-handle/errorhandle.service';
import { ShipperConfigService } from 'src/app/core/services/sci/shipper-config/shipper-config.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { LocationPermissionUtils } from 'src/app/core/utils/permissions/location-permissions/location-permissions.utils';
import { BUTTON_CLICK } from '../../modal/warning-modal/warning-modal.constants';
import {
  ERROR_TEXT,
  IMAGE_SRC,
  NO_RESULTS,
  SELECT_ALL_TEXT,
  STATUS_OPTIONS,
} from '../../shared-constants';
import {
  ColumnIds,
  ColumnText,
  CONSTS,
} from './shipper-config-table.constants';

@Component({
  selector: 'app-shipper-config-table',
  templateUrl: './shipper-config-table.component.html',
  styleUrl: './shipper-config-table.component.scss',
  standalone: false,
})
export class ShipperConfigTableComponent implements OnInit, AfterViewInit {
  @Input() headerText = CONSTS.WELCOME_TEXT;
  @Input() hasRadioButton = false;
  @Input() radioHeader!: string;
  @Input() firstOptionValue!: string;
  @Input() firstOptionName!: string;
  @Input() secondOptionValue!: string;
  @Input() secondOptionName!: string;
  @Input() radioModel!: AdminSearchCriteria;
  @Output() radioButtonChange = new EventEmitter<Event>();

  private userService = inject(UserService);
  readonly destroy = inject(DestroyRef);

  readonly displayedColumns = Object.values(ColumnIds);
  readonly COLUMNTEXT = ColumnText;
  readonly STATUS_OPTIONS = STATUS_OPTIONS;
  readonly IMAGES = IMAGE_SRC;
  readonly NO_RESULTS = NO_RESULTS;
  readonly ERROR_TEXT = ERROR_TEXT;
  readonly SELECT_ALL = SELECT_ALL_TEXT;
  readonly CONSTS = CONSTS;
  readonly SPECIAL_CHARS = SpecialCharacters;

  // Create shipper config table columns
  shipperIdData = this.commonUtils.createTableColumn(ColumnIds.SHIPPER_ID);
  codeData = this.commonUtils.createTableColumn(ColumnIds.CODE);
  countryData = this.commonUtils.createTableColumn(ColumnIds.ORIGIN_COUNTRY);
  slicData = this.commonUtils.createTableColumn(ColumnIds.ORIGIN_SLIC);
  // image source variables
  checkboxSrc = IMAGE_SRC.CHECKBOX;
  chevronSrc = IMAGE_SRC.CHEVRON;
  activeSrc = IMAGE_SRC.ACTIVE;
  cancelSrc = IMAGE_SRC.CANCEL;

  removeLocation = false;
  removeLocationConfirm = false;
  // selectedLocation!: SelectedLocationType;

  dataSource = new MatTableDataSource<ShipperConfig>();
  removeUser = false;
  edit = false;
  currentUser!: UserAdmin | undefined;
  shipperConfigData: ShipperConfig[] = [];
  resetSelectDropdowns = new BehaviorSubject<boolean>(false);
  dropdownOpen = [false, false, false];
  timeZoneDropDown: Items[] = [];
  editedRecordIndex!: number;

  constructor(
    private readonly dialog: MatDialog,
    private readonly shipperConfigService: ShipperConfigService,
    private readonly errorHandler: ErrorhandleService,
    public commonUtils: CommonUtils,
    private readonly roleUtils: LocationPermissionUtils,
  ) {}

  ngOnInit(): void {
    this.currentUser = {} as UserAdmin;
    this.commonUtils.updateLoadingModal(true);
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((user) => !!user),
        switchMap((user) => {
          this.currentUser = user;
          return of(null);
          // return this.getLocationData(user?.locationGroupId || 0);
        }),
        finalize(() => this.commonUtils.updateLoadingModal(false)),
      )
      .subscribe();
    this.getShipperConfigData();
  }

  getShipperConfigData() {
    this.shipperConfigService
      .getShipperConfig('', this.currentUser?.loginId)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((resp) => {
        this.dataSource.data = [...resp];
      });
  }

  ngAfterViewInit() {
    this.dataSource.filterPredicate = this.commonUtils.createFilter();
  }

  get canEditDeleteShipperConfig() {
    return true;
  }

  get isAddShipperConfigDisabled() {
    const role = this.currentUser?.roleName;
    const permissions = this.roleUtils.canEditLocation(role);
    return permissions;
  }

  onRemoveShipperConfig(shipperId: string) {
    this.commonUtils
      .openWarningModal({
        header: this.CONSTS.REMOVE_SHIPPER_CONFIG,
        body: this.CONSTS.CONFIRM_REMOVE,
        extraText: `Shipper Id: ${shipperId}`,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((resp) => {
        if (resp.button === BUTTON_CLICK.ACCEPT) {
          this.shipperConfigService
            .deleteShipperConfig(shipperId)
            .subscribe(() => this.getShipperConfigData());
        }
      });
  }

  clearAllFilters() {
    this.commonUtils.clearAllFilters(this.dataSource, this.shipperConfigData, [
      this.shipperIdData.formControl,
    ]);
    this.resetSelectDropdowns.next(true);
  }

  search() {
    this.commonUtils.searchFilter([this.shipperIdData], this.dataSource);
  }

  onStatusChangeForDropdowns(
    $event: string | boolean | unknown[],
    field: TableColumn,
  ) {
    field.ids[0] = $event as unknown as Items;
    this.search();
  }

  addShipperConfig() {
    this.edit = false;
    this.commonUtils.updateBanner(false);
    this.openShipperConfigPopup$({} as ShipperConfig, false);
  }

  editShipperConfiguration(data: ShipperConfig, index: number) {
    // Todo: depends on unfinished back-end work
    this.editedRecordIndex = index;
    this.commonUtils.updateBanner(false);
    this.openShipperConfigPopup$(data, true);

    // this.locationService
    //   .getLocationProfile(data.locationCode)
    //   .pipe(
    //     take(1),
    //     mergeMap((profile) => {
    //       return this.openShipperConfigPopup$(profile?.response, true);
    //     }),
    //     catchError(() => {
    //       this.commonUtils.updateBannerType(BANNER_TYPE.API_ERROR);
    //       return of(false);
    //     }),
    //   )
    //   .subscribe();
  }

  openShipperConfigPopup$(profile: ShipperConfig, edit?: boolean) {
    this.dialog
      .open(ShipperConfigPopupComponent, {
        disableClose: true,
        width: '60vw',
        minWidth: '550px',
        data: {
          config: profile,
          edit: edit,
        },
      })
      .afterClosed()
      .pipe(
        mergeMap((data: any) => {
          return data ? of(true) : /* Handle closed early state */ of(false);
        }),
        catchError((error) => {
          return this.errorHandler.handleError<ShipperConfigTableComponent>(
            error,
            error.error.code,
            'Shipper Config: failed to add config',
          );
        }),
      )
      .subscribe((res) => {
        if (res) {
          this.getShipperConfigData();
        }
      });
  }

  emitRadioButtonChange($event: Event) {
    this.radioButtonChange.emit($event);
  }

  get isFilterEnabled() {
    return this.shipperIdData.formControl.value !== '';
  }
}
